import { useMemo, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useMetaDataContext } from '../../../../../state-management/contexts/MetaDataContext'
import { TaxLiabilityDataResponseType } from '../../../types/investmentApi.types'
import { roundOffAndFormatNumberWithCurrency } from '../../../../../utils/utils'
import { useBottomSheet } from '../../../../../state-management/contexts/BottomSheetContext'
import { TaxLiabilityL2BreakupBottomSheet } from '../../../../../components/BottomSheetComponents/tax-liability/TaxLiabilityL2BreakupBottomSheet'
import { TAX_LIABILITY_BREAKUP_HEADING_ID } from '../../../../../components/BottomSheetComponents/tax-liability/constants'
import { fetchTaxLiability } from '../../../repository/userInvestmentApi'

type TaxLiabilityData = TaxLiabilityDataResponseType & {
  preTaxValue?: number
  taxLiability?: number
  postTaxValue?: number
}

export default function TaxLiabilityV2({ temporaryFamilyAccessToken }: { temporaryFamilyAccessToken: string }) {
  const { isin, data: metadata } = useMetaDataContext()
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()
  const viewBreakupTriggerRef = useRef<HTMLButtonElement>(null)
  const { data: taxLiabilityApiResponseData } = useQuery<any, Error>({
    queryKey: ['taxLiability', isin, temporaryFamilyAccessToken],
    queryFn: () => fetchTaxLiability(isin, temporaryFamilyAccessToken),
    retry: 1,
    enabled: !!isin,
    refetchOnWindowFocus: false
  })
  const taxLiabilityResponseData = taxLiabilityApiResponseData?.data as TaxLiabilityDataResponseType | undefined
  const taxLiabilityData: TaxLiabilityData | null = useMemo(() => {
    if (taxLiabilityResponseData) {
      const currentValue = taxLiabilityResponseData?.currentValue ?? 0
      const exitLoad = taxLiabilityResponseData?.exitLoad ?? 0
      const preTaxValue = currentValue - exitLoad
      const stcgTaxableAmount = taxLiabilityResponseData?.stcgTaxInfo?.taxableAmount ?? 0
      const ltcgTaxableAmount = taxLiabilityResponseData?.ltcgTaxInfo?.taxableAmount ?? 0
      const taxLiability = stcgTaxableAmount + ltcgTaxableAmount

      return {
        ...taxLiabilityResponseData,
        preTaxValue,
        taxLiability,
        postTaxValue: preTaxValue - taxLiability
      } as TaxLiabilityData
    }

    return null
  }, [taxLiabilityResponseData])

  const isUnavailable = useMemo(() => {
    return taxLiabilityData?.fundType?.toLowerCase() === 'elss' && taxLiabilityData?.ltcgTaxInfo?.investedAmount === 0
  }, [taxLiabilityData])

  if (!taxLiabilityResponseData) return null

  const handleDismissBreakup = () => {
    closeBottomSheet()
    window.setTimeout(() => viewBreakupTriggerRef.current?.focus(), 0)
  }

  const handleOpenBreakup = () => {
    if (isUnavailable) return

    openBottomSheet(
      <TaxLiabilityL2BreakupBottomSheet
        taxLiabilityData={taxLiabilityData ?? ({} as TaxLiabilityData)}
        heading='Tax Liability'
        onDismiss={handleDismissBreakup}
        fundCategory={metadata?.fundDetails?.category}
      />,
      handleDismissBreakup,
      { drawerTitleId: TAX_LIABILITY_BREAKUP_HEADING_ID }
    )
  }

  return (
    <button
      ref={viewBreakupTriggerRef}
      type='button'
      onClick={handleOpenBreakup}
      disabled={isUnavailable}
      aria-label={
        isUnavailable
          ? 'Tax Liability, unavailable since investment is in lock-in period'
          : `View tax liability breakup, ${roundOffAndFormatNumberWithCurrency(taxLiabilityData?.taxLiability ?? 0)}`
      }
      className='rounded-xl border border-[#EBF2F2] w-full p-4 animate-fade-150 text-left bg-white'
    >
      <div className='flex justify-between gap-10'>
        <div className='flex flex-col text-[13px] text-[#3D3D3D] font-[400]'>
          <div aria-hidden='true'> Tax Liability</div>

          {isUnavailable ? (
            <div aria-hidden='true'>Unavailable since investment is in lock-in period</div>
          ) : (
            <div aria-hidden='true' className='underline'>
              View breakup
            </div>
          )}
        </div>
        <div
          aria-hidden='true'
          className='text-[#232323] flex-1 text-[13px] font-[500] text-right'
        >
          <span className='text-black'>
            {roundOffAndFormatNumberWithCurrency(taxLiabilityData?.taxLiability ?? 0)}
          </span>{' '}
        </div>
      </div>
    </button>
  )
}
